/**
 * As 19 telas do produto, em UMA fonte só.
 *
 * O menu, o roteador e o teste de rotas leem daqui. Se o menu tivesse a sua
 * lista e o roteador a dele, a primeira divergência viraria um 404 — que é
 * exatamente o que este ciclo não pode produzir.
 *
 * `construida: false` NÃO é um buraco: é uma rota real que renderiza
 * "ainda não construído", dizendo o nome da tela e o que ela vai fazer.
 * Tela que não existe tem que dizer que não existe.
 */

import ConstructionOutlined from '@mui/icons-material/ConstructionOutlined';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import EventRepeatOutlined from '@mui/icons-material/EventRepeatOutlined';
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';
import FolderSharedOutlined from '@mui/icons-material/FolderSharedOutlined';
import FolderSpecialOutlined from '@mui/icons-material/FolderSpecialOutlined';
import GavelOutlined from '@mui/icons-material/GavelOutlined';
import HandshakeOutlined from '@mui/icons-material/HandshakeOutlined';
import InsightsOutlined from '@mui/icons-material/InsightsOutlined';
import ManageSearchOutlined from '@mui/icons-material/ManageSearchOutlined';
import MedicalInformationOutlined from '@mui/icons-material/MedicalInformationOutlined';
import MarkEmailUnreadOutlined from '@mui/icons-material/MarkEmailUnreadOutlined';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import MoveToInboxOutlined from '@mui/icons-material/MoveToInboxOutlined';
import PendingActionsOutlined from '@mui/icons-material/PendingActionsOutlined';
import PolicyOutlined from '@mui/icons-material/PolicyOutlined';
import PrecisionManufacturingOutlined from '@mui/icons-material/PrecisionManufacturingOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import type { SvgIconComponent } from '@mui/icons-material';

export interface EntradaDeNavegacao {
  readonly chave: string;
  readonly rotulo: string;
  readonly caminho: string;
  readonly icone: SvgIconComponent;
  /** `false` → rota real com estado "ainda não construído". Nunca 404. */
  readonly construida: boolean;
  /** O que a tela vai fazer. É o texto do stub — some quando ela nascer. */
  readonly promessa: string;
  /** Agrupamento visual da barra lateral. */
  readonly grupo: 'Operação do caso' | 'Financeiro' | 'Plataforma';
}

export const mapaDeNavegacao: readonly EntradaDeNavegacao[] = [
  {
    chave: 'visao-geral',
    rotulo: 'Visão geral',
    caminho: '/visao-geral',
    icone: DashboardOutlined,
    construida: true,
    promessa: 'O que o dia cobra: o que precisa de gente, o que anda sozinho e o que está travado.',
    grupo: 'Operação do caso',
  },
  {
    chave: 'casos',
    rotulo: 'Casos',
    caminho: '/casos',
    icone: FolderSharedOutlined,
    construida: true,
    promessa: 'A fila de casos, com prazo, status e caminho de reprocesso.',
    grupo: 'Operação do caso',
  },
  {
    chave: 'fila-de-triagem',
    rotulo: 'Fila de triagem',
    caminho: '/fila-de-triagem',
    icone: FactCheckOutlined,
    construida: true,
    promessa: 'O que chegou por e-mail, de quem é, o prazo, e o que o interpretador leu de cada campo.',
    grupo: 'Operação do caso',
  },
  {
    chave: 'captacao-siai',
    rotulo: 'Captação via SIAI',
    caminho: '/captacao-siai',
    icone: MoveToInboxOutlined,
    construida: true,
    promessa: 'Entrada de casos vinda do SIAI, com a fila de recebimento e o que falhou na captação.',
    grupo: 'Operação do caso',
  },
  {
    // TP-065 (NR-196). O aviso à coordenação é esta tela, com o número de aceites vencidos no menu: ainda não há canal
    // de envio (é o NR-27).
    chave: 'fila-da-avaliacao',
    rotulo: 'Fila da avaliação',
    caminho: '/fila-da-avaliacao',
    icone: PendingActionsOutlined,
    construida: true,
    promessa: 'As avaliações acionadas, com o prazo para a avaliadora aceitar e os aceites vencidos no topo.',
    grupo: 'Operação do caso',
  },
  {
    chave: 'conduta-medica',
    rotulo: 'Conduta médica',
    caminho: '/conduta-medica',
    icone: MedicalInformationOutlined,
    construida: true,
    promessa: 'Registro e acompanhamento da conduta, com a decisão clínica que sustenta o caso.',
    grupo: 'Operação do caso',
  },
  {
    chave: 'pendencia-dossie',
    rotulo: 'Pendência de dossiê',
    caminho: '/pendencia-dossie',
    icone: FolderSpecialOutlined,
    construida: false,
    promessa: 'O que falta no dossiê de cada caso, desde quando falta, e de quem é a bola.',
    grupo: 'Operação do caso',
  },
  {
    chave: 'ci-diaria',
    rotulo: 'CI diária',
    caminho: '/ci-diaria',
    icone: EventRepeatOutlined,
    construida: true,
    promessa: 'O retorno da operadora do dia: o que cada carta decidiu, e a confirmação do que isso faz com o caso.',
    grupo: 'Operação do caso',
  },
  {
    chave: 'faturamento',
    rotulo: 'Faturamento',
    caminho: '/faturamento',
    icone: ReceiptLongOutlined,
    construida: true,
    promessa: 'Fechamento e envio de faturamento, com conferência antes de qualquer saída externa.',
    grupo: 'Financeiro',
  },
  {
    chave: 'glosa-e-recurso',
    rotulo: 'Glosa e recurso',
    caminho: '/glosa-e-recurso',
    icone: GavelOutlined,
    construida: true,
    promessa: 'Glosas recebidas, prazo de recurso e o histórico do que já foi contestado.',
    grupo: 'Financeiro',
  },
  {
    chave: 'fechamento-de-prestadores',
    rotulo: 'Fechamento de prestadores',
    caminho: '/fechamento-de-prestadores',
    icone: HandshakeOutlined,
    construida: true,
    promessa: 'Fechamento do período por prestador, com divergências separadas do que já bateu.',
    grupo: 'Financeiro',
  },
  {
    chave: 'operacao',
    rotulo: 'Operação (ver o robô)',
    caminho: '/operacao',
    icone: PrecisionManufacturingOutlined,
    construida: false,
    promessa:
      'A fila de execução das automações: status, erro, DLQ e reprocesso pelo correlation_id original. Nasce quando existir robô rodando — painel de operação sobre fila inventada seria exatamente a ficção que a regra "se não dá pra ver o robô, não é feature, é dívida" existe para impedir.',
    grupo: 'Plataforma',
  },
  {
    // NR-141. A fila de erro da coleta e da triagem de e-mail. NÃO é a
    // "Operação (ver o robô)" acima: aquela promete a fila de execução das
    // automações, que ainda não existe; esta mostra o que já existe.
    chave: 'falhas-de-email',
    rotulo: 'Falhas de e-mail',
    caminho: '/falhas-de-email',
    icone: MarkEmailUnreadOutlined,
    construida: true,
    promessa: 'Os e-mails que a coleta ou a triagem não conseguiram processar, e o reprocesso da coleta.',
    grupo: 'Plataforma',
  },
  {
    // TP-049. O PDF do contrato vira fonte da base de conhecimento e, do
    // passo 1 do compilador em diante, vira itens contratuais consultáveis.
    chave: 'contratos',
    rotulo: 'Contratos',
    caminho: '/contratos',
    icone: DescriptionOutlined,
    construida: true,
    promessa: 'Envio do PDF do contrato, acompanhamento da fonte, itens contratuais e comparação entre contratos-irmãos.',
    grupo: 'Plataforma',
  },
  {
    chave: 'governanca-de-ia',
    rotulo: 'Governança de IA',
    caminho: '/governanca-de-ia',
    icone: PolicyOutlined,
    construida: false,
    promessa: 'Versão de prompt, custo, taxa de acerto e o log de 100% das chamadas de modelo.',
    grupo: 'Plataforma',
  },
  {
    chave: 'operadora-e-configuracao',
    rotulo: 'Operadora é configuração',
    caminho: '/operadora-e-configuracao',
    icone: TuneOutlined,
    construida: false,
    promessa: 'Regra de operadora como dado, não como código: prazo, layout, exigência e exceção.',
    grupo: 'Plataforma',
  },
  {
    chave: 'base-de-conhecimento',
    rotulo: 'Base de conhecimento',
    caminho: '/base-de-conhecimento',
    icone: MenuBookOutlined,
    construida: true,
    promessa: 'O procedimento escrito que a operação consulta e que alimenta a automação.',
    grupo: 'Plataforma',
  },
  {
    chave: 'dashboard-executivo',
    rotulo: 'Dashboard executivo',
    caminho: '/dashboard-executivo',
    icone: InsightsOutlined,
    construida: true,
    promessa: 'Indicadores de ponta a ponta: volume, prazo, glosa e o que a automação economizou.',
    grupo: 'Plataforma',
  },
  {
    // NR-114. Aberta a todo usuário logado por enquanto — o perfilamento
    // (US-1202) vai restringir.
    chave: 'trilha-de-acesso',
    rotulo: 'Trilha de acesso',
    caminho: '/trilha-de-acesso',
    icone: ManageSearchOutlined,
    construida: true,
    promessa: 'Quem viu ou alterou dado de paciente, quando e o quê — a trilha LGPD, que não se edita nem se apaga.',
    grupo: 'Plataforma',
  },
];

export const gruposDeNavegacao = ['Operação do caso', 'Financeiro', 'Plataforma'] as const;

/** Onde `/` cai. */
export const CAMINHO_INICIAL = '/visao-geral';

/**
 * De onde a pessoa abriu o Caso.
 *
 * O detalhe é alcançado por dois lugares — a tabela de `/casos` e o quadro da
 * captação — e "voltar" tem que devolver para o que a pessoa estava olhando.
 * Vai no `state` da navegação, e não no histórico (`navigate(-1)`), porque
 * link colado na barra de endereço não tem histórico: aí vale o padrão.
 */
export interface OrigemDoCaso {
  readonly caminho: string;
  readonly rotulo: string;
}

export const ORIGENS_DO_CASO = {
  fila: { caminho: '/casos', rotulo: 'Voltar para a fila' },
  captacao: { caminho: '/captacao-siai', rotulo: 'Voltar para a captação' },
  ciDiaria: { caminho: '/ci-diaria', rotulo: 'Voltar para a CI diária' },
} as const satisfies Record<string, OrigemDoCaso>;

/** Sem origem declarada (link colado, F5): a fila é o destino neutro. */
export const ORIGEM_PADRAO: OrigemDoCaso = ORIGENS_DO_CASO.fila;

/** A fila de Casos — pra quem sai do detalhe, não do menu. */
export const CAMINHO_DA_FILA_DE_CASOS = '/casos';

/** A lista da fila de triagem — pra quem sai do detalhe, não do menu. */
export const CAMINHO_DA_FILA_DE_TRIAGEM = '/fila-de-triagem';

/** Detalhe do caso — rota real, alcançada pela lista, fora do menu. */
export const caminhoDoCaso = (id: string): string => `/casos/${encodeURIComponent(id)}`;

/** Detalhe da triagem — rota real, alcançada pela lista, fora do menu. */
export const caminhoDaTriagem = (ticketId: string): string => `/fila-de-triagem/${encodeURIComponent(ticketId)}`;

/** Revisão de regras de uma fonte (`TP-050`) — rota real, alcançada pela lista de fontes, fora do menu. */
export const caminhoDaRevisaoDaFonte = (fonteId: string): string => `/contratos/fontes/${encodeURIComponent(fonteId)}/revisao`;

/** Parametrização de um contrato (`TP-051`, T10/T11) — rota real, alcançada pela tela de Contratos, fora do menu. */
export const caminhoDaParametrizacao = (contratoId: string): string => `/contratos/${encodeURIComponent(contratoId)}/parametrizacao`;

/** Detalhe de uma conferência de faturamento (`TP-052`, T9) — rota real, alcançada pela lista, fora do menu. */
export const caminhoDaConferenciaDeFaturamento = (conferenciaId: string): string =>
  `/faturamento/${encodeURIComponent(conferenciaId)}`;

export const ICONE_EM_CONSTRUCAO = ConstructionOutlined;
